import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { SessionService } from '@core/config/session';
import { UserModel } from '@core/store/user/user.model';
import { UserService } from '@core/store/user/user.service';
import { Observable, catchError, defaultIfEmpty, map, of, tap } from 'rxjs';

/**
 * The user, if there is one.
 *
 * `AuthGuard` loads the user as a side effect of guarding, which is fine while
 * every route is guarded. The briefing pages are not, and they still need the
 * store populated for a signed-in visitor -- so the load moves here, where "no
 * user" is an answer rather than a failure.
 *
 * Three ways there is no user, and all three resolve `null` rather than
 * failing:
 *
 *  - no session cookie, so nothing to ask for;
 *  - the request is refused, e.g. an expired cookie -- that should render the
 *    signed-out briefing, not an error page;
 *  - `UserService.loadUser` returns EMPTY under SSR, to keep the server off the
 *    network. An Angular resolver that returns an empty observable **cancels
 *    the navigation**, so without `defaultIfEmpty` a signed-in server render of
 *    a briefing would produce a blank page rather than a briefing.
 *
 * `loadUser` is typed `Observable<Object>` -- it is a bare `http.get` with no
 * generic. `AuthGuard` casts it to `any` at its own call site; this narrows it
 * once, here, rather than spreading the cast.
 */
export const optionalUserResolver: ResolveFn<UserModel | null> = (): Observable<UserModel | null> => {
  const session = inject(SessionService);
  const users = inject(UserService);

  if (!session.hasSession()) {
    return of(null);
  }

  return users.loadUser().pipe(
    map(user => user as UserModel),
    tap(user => users.setUser(user)),
    defaultIfEmpty(null as UserModel | null),
    catchError(() => of(null)),
  );
};
